import React, { useState, useEffect } from "react";
import { NavLink, Outlet, Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Factory,
  Users,
  UserCircle2,
  LogOut,
  Search,
  Building2,
  Inbox,
  Menu,
  ShoppingCart,
  ClipboardList,
  Bell,
  Send,
  Megaphone,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useAgency } from "@/context/AgencyContext";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import api, { photoUrl } from "@/lib/api";
import AIStatusBanner from "@/components/AIStatusBanner";
import AgencySwitcher from "@/components/AgencySwitcher";
import WeProudlyRepresent from "@/components/WeProudlyRepresent";
import CartDrawer from "@/components/CartDrawer";
import InboxBell from "@/components/InboxBell";

const NAV_ALL = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, roles: ["admin", "editor"] },
  { to: "/manufacturers", label: "Manufacturers", icon: Factory, roles: ["admin", "editor"] },
  { to: "/search", label: "Search", icon: Search, roles: ["admin", "editor", "agency_user", "distributor"] },
  { to: "/quotes", label: "My quotes", icon: ClipboardList, roles: ["distributor"] },
  { to: "/inbox", label: "Inbox", icon: Bell, roles: ["admin", "agency_user", "distributor"] },
  { to: "/approvals", label: "Approvals", icon: Inbox, roles: ["admin", "agency_user"] },
  { to: "/marketing", label: "Marketing", icon: Megaphone, roles: ["admin", "agency_user"] },
  { to: "/notifications", label: "Send push", icon: Send, roles: ["admin"] },
  { to: "/agencies", label: "Agencies", icon: Building2, roles: ["admin"] },
  { to: "/users", label: "Users", icon: Users, roles: ["admin"] },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const { activeId: ctxActiveId, active: ctxActive } = useAgency();
  const { itemCount, setDrawerOpen } = useCart();
  const location = useLocation();
  const role = user?.role;
  const isAgencyUser = role === "agency_user";
  const isDistributor = role === "distributor";
  const hideSwitcher = isAgencyUser || isDistributor;
  const navItems = NAV_ALL.filter((n) => n.roles.includes(role));
  const [mobileOpen, setMobileOpen] = useState(false);

  // Pending approvals badge — only meaningful for admin + agency_user.
  // Refetches on every route change (cheap) so the badge clears immediately
  // after an approve/deny action, plus a 60s safety poll.
  const canSeeApprovals = role === "admin" || role === "agency_user";
  const canSeeInbox = role === "admin" || role === "agency_user" || role === "distributor";
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [unreadInbox, setUnreadInbox] = useState(0);
  useEffect(() => {
    if (!canSeeApprovals && !canSeeInbox) return undefined;
    let cancelled = false;
    const fetchCounts = () => {
      if (canSeeApprovals) {
        api
          .get("/registrations/counts")
          .then(({ data }) => {
            if (!cancelled) setPendingApprovals(data?.pending || 0);
          })
          .catch(() => {});
      }
      if (canSeeInbox) {
        api
          .get("/notifications/inbox/unread-count")
          .then(({ data }) => {
            if (!cancelled) setUnreadInbox(data?.unread || 0);
          })
          .catch(() => {});
      }
    };
    fetchCounts();
    const interval = setInterval(fetchCounts, 60_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [canSeeApprovals, canSeeInbox, location.pathname]);

  // For admin/editor the active agency comes from the context (switcher).
  // For agency_user / distributor it's the agency they belong to.
  const sidebarAgencyId =
    role === "admin" || role === "editor" ? ctxActiveId : user?.agency_id || null;

  // Agency name for the centered top-bar title.
  //  - admin / editor: comes from AgencyContext (the active selection)
  //  - agency_user / distributor: their own agency — fetch by id
  const [ownAgencyName, setOwnAgencyName] = useState("");
  useEffect(() => {
    if (role === "admin" || role === "editor") return undefined;
    if (!sidebarAgencyId) {
      setOwnAgencyName("");
      return undefined;
    }
    let cancelled = false;
    api
      .get(`/rep-agencies/${sidebarAgencyId}`)
      .then(({ data }) => {
        if (!cancelled) setOwnAgencyName(data?.name || "");
      })
      .catch(() => {
        if (!cancelled) setOwnAgencyName("");
      });
    return () => {
      cancelled = true;
    };
  }, [role, sidebarAgencyId]);

  const topBarAgencyName =
    role === "admin" || role === "editor"
      ? ctxActive?.name || ""
      : ownAgencyName;

  // Auto-close mobile drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const NavList = ({ onItemClick }) => (
    <nav className="flex-1 py-4 px-3 space-y-1">
      {navItems.map((item) => {
        const Icon = item.icon;
        let badgeCount = 0;
        if (item.to === "/approvals" && pendingApprovals > 0) {
          badgeCount = pendingApprovals;
        } else if (item.to === "/inbox" && unreadInbox > 0) {
          badgeCount = unreadInbox;
        }
        const showBadge = badgeCount > 0;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onItemClick}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-medium transition-colors ${
                isActive
                  ? "bg-slate-800 text-white"
                  : "text-slate-400 hover:bg-slate-800/60 hover:text-white"
              }`
            }
          >
            <Icon className="w-4 h-4" />
            <span className="flex-1">{item.label}</span>
            {showBadge && (
              <span
                className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-sm bg-amber-500 text-slate-900 text-[10px] font-bold font-mono-data"
                data-testid={`nav-${item.to.replace(/^\//, "")}-badge`}
              >
                {badgeCount > 99 ? "99+" : badgeCount}
              </span>
            )}
          </NavLink>
        );
      })}
    </nav>
  );

  const NavFooter = ({ onItemClick }) => (
    <div className="px-3 py-3 border-t border-slate-800">
      <Link
        to="/profile"
        onClick={onItemClick}
        data-testid="nav-profile"
        className={`flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-medium transition-colors ${
          location.pathname === "/profile"
            ? "bg-slate-800 text-white"
            : "text-slate-400 hover:bg-slate-800/60 hover:text-white"
        }`}
      >
        <UserCircle2 className="w-4 h-4" />
        Profile
      </Link>
      <button
        onClick={() => {
          if (onItemClick) onItemClick();
          logout();
        }}
        data-testid="logout-btn"
        className="mt-1 w-full flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-medium text-slate-400 hover:bg-slate-800/60 hover:text-white transition-colors"
      >
        <LogOut className="w-4 h-4" />
        Sign out
      </button>
    </div>
  );

  const SidebarHeader = () => {
    // Dynamic per-agency logo: agency_logo_photo_id (uploaded) takes
    // priority, then agency_logo_url (legacy text field), then the generic
    // platform logo as fallback.
    const agencyLogoSrc =
      (user?.agency_logo_photo_id && photoUrl(user.agency_logo_photo_id)) ||
      user?.agency_logo_url ||
      null;
    const logoSrc = agencyLogoSrc || "/byrd-logo.png";
    const altText = agencyLogoSrc
      ? user?.agency_name || "Rep agency"
      : "My Rep Agency";    return (
      <div className="h-24 px-3 flex items-center justify-center bg-white border-b border-slate-800">
        <img
          src={logoSrc}
          alt={altText}
          className="h-24 w-auto object-contain"
          data-testid="sidebar-logo"
          data-agency-logo={agencyLogoSrc ? "true" : "false"}
        />
      </div>
    );
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop Sidebar */}
      <aside
        data-testid="sidebar"
        className="hidden md:flex md:flex-col w-64 bg-slate-900 text-slate-300 sticky top-0 h-screen"
      >
        <SidebarHeader />
        <div className="flex-1 flex flex-col min-h-0 overflow-y-auto">
          <NavList />
          <WeProudlyRepresent agencyId={sidebarAgencyId} variant="sidebar" />
        </div>
        <NavFooter />
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header
          className="bg-white border-b border-border flex items-center justify-between px-4 md:px-6 sticky top-0 z-30 relative"
          style={{
            paddingTop: "env(safe-area-inset-top)",
            height: "calc(4rem + env(safe-area-inset-top))",
          }}
        >
          <div className="flex items-center gap-3">
            {/* Mobile hamburger */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="md:hidden -ml-2"
                  data-testid="mobile-menu-trigger"
                  aria-label="Open menu"
                >
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="left"
                className="p-0 w-72 bg-slate-900 text-slate-300 border-r-0 flex flex-col [&>button]:text-white"
                data-testid="mobile-nav-drawer"
              >
                <SidebarHeader />
                <div className="flex-1 flex flex-col min-h-0 overflow-y-auto">
                  <NavList onItemClick={() => setMobileOpen(false)} />
                  <WeProudlyRepresent
                    agencyId={sidebarAgencyId}
                    variant="sidebar"
                    onLinkClick={() => setMobileOpen(false)}
                  />
                </div>
                <NavFooter onItemClick={() => setMobileOpen(false)} />
              </SheetContent>
            </Sheet>
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">
              {breadcrumbFor(location.pathname)}
            </div>
          </div>
          {/* Centered agency name — anchored absolutely so left/right groups stay balanced */}
          {topBarAgencyName && (
            <div
              className="hidden md:flex absolute left-1/2 -translate-x-1/2 items-center pointer-events-none"
              style={{ top: "env(safe-area-inset-top)", height: "4rem" }}
              data-testid="topbar-agency-name"
            >
              <span className="text-sm font-display font-semibold tracking-tight text-slate-900 truncate max-w-[40vw]">
                {topBarAgencyName}
              </span>
            </div>
          )}
          <div className="flex items-center gap-3">
            {!hideSwitcher && <AgencySwitcher />}
            <InboxBell />
            {/* Cart icon — visible to anyone who can search */}
            {(role === "distributor" || role === "admin" || role === "editor") && (
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                className="relative w-9 h-9 rounded-sm bg-slate-100 hover:bg-slate-200 text-slate-900 flex items-center justify-center transition-colors"
                aria-label={`Open quote cart${itemCount ? ` (${itemCount} items)` : ""}`}
                data-testid="cart-trigger"
              >
                <ShoppingCart className="w-4 h-4" />
                {itemCount > 0 && (
                  <span
                    className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-slate-900 text-[10px] font-bold flex items-center justify-center"
                    data-testid="cart-badge"
                  >
                    {itemCount > 99 ? "99+" : itemCount}
                  </span>
                )}
              </button>
            )}
            <div className="hidden sm:flex flex-col items-end justify-center leading-none gap-0.5">
              <span className="text-sm font-medium text-slate-900" data-testid="topbar-username">
                {user?.name}
              </span>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {role === "agency_user"
                  ? "Agency user"
                  : role === "distributor"
                  ? "Distributor"
                  : role}
              </span>
            </div>
            <div className="w-9 h-9 rounded-sm bg-slate-900 text-white flex items-center justify-center font-display font-bold text-sm">
              {user?.name?.slice(0, 1)?.toUpperCase() || "?"}
            </div>
          </div>
        </header>

        <AIStatusBanner />

        <main
          className="flex-1 p-4 md:p-8 max-w-[1600px] w-full mx-auto"
          style={{ paddingBottom: "calc(2rem + env(safe-area-inset-bottom))" }}
        >
          <Outlet />
        </main>
      </div>

      {/* Quote cart drawer — mounted once at shell level */}
      <CartDrawer />
    </div>
  );
}

function breadcrumbFor(path) {
  if (path === "/") return "Dashboard";
  if (path.startsWith("/manufacturers")) return "Manufacturers";
  if (path.startsWith("/search")) return "Search";
  if (path.startsWith("/agencies")) return "Agencies";
  if (path.startsWith("/users")) return "Users";
  if (path.startsWith("/approvals")) return "Approvals";
  if (path.startsWith("/quotes")) return "My quotes";
  if (path.startsWith("/profile")) return "Profile";
  return "";
}
